import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import useGenericEndpoint from '../hooks/api/useGenericEndpoint'
import { addTimezoneOffsetToTimestamp } from '../utils/date'
import DeleteConfirmModal from '../customer/components/DeleteConfirmModal'
import { authedAxios, deleteCookie } from '../utils'
import { grey } from '@mui/material/colors'
import useIsAdmin from '../hooks/useIsAdmin'
import useAlert from '../hooks/useAlert'
import BillingHistory from './BillingHistory'
import EditCard from './EditCard'
import { useSearchParams } from 'react-router-dom'
import UnpaidView from './UnpaidView'

const Billing = ({
  billingQuery: { data, isLoading, hasLoadError, refetch, setIsLoading },
  nextDateDisplay,
  isBeforeNextDate,
  isUnpaid,
}) => {
  const [reenabling, setReenabling] = useState(false)
  const [canceling, setCanceling] = useState(false)
  const [editingCard, setEditingCard] = useState(false)

  const { payment, schedule } = data || {}

  const { createGenericErrorAlert, createAlert } = useAlert()

  const cancelSubscription = () => {
    setIsLoading(true)
    authedAxios
      .put(`/api/billing/cancel/${schedule.key}`)
      .then(() => {
        setCanceling(false)
        return refetch().then(() => {
          createAlert({
            message:
              'Successfully canceled subscription. Return to this page to reenable at any time',
          })
        })
      })
      .catch(createGenericErrorAlert)
  }

  const enableSubscription = () => {
    setIsLoading(true)
    authedAxios
      .put(`/api/billing/enable/${schedule?.key}`)
      .then(() => {
        setReenabling(false)
        return refetch().then(() => {
          if (isUnpaid) {
            deleteCookie('access_token')
            window.location.replace('/')
            return
          }
          createAlert({
            message: 'Successfully enabled subscription.',
          })
        })
      })
      .catch(createGenericErrorAlert)
  }

  return (
    <Box>
      <Card>
        <CardContent>
          {payment && (
            <>
              <Typography variant='h5' sx={{ mb: 2 }}>
                Payment information
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: '18px' }}>
                  <b>Card</b>
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography>
                    {payment.card_type} ************{payment.ccnum4last}
                  </Typography>
                  <Typography>
                    Expires{' '}
                    {payment.expires.substr(0, 2) +
                      '/' +
                      payment.expires.substr(2, 2)}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
          {schedule && (
            <>
              <Typography sx={{ fontSize: '18px' }}>
                <b>Recurring payment</b>
              </Typography>
              <Box sx={{ pl: 2 }}>
                {schedule?.enabled === '1' ? (
                  <>
                    <Typography>Amount: ${schedule.amount}</Typography>
                    <Typography>
                      Next payment date: {nextDateDisplay}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Box sx={{ color: grey[700], fontStyle: 'italic' }}>
                      <Typography>
                        <b>Disabled</b> -
                        <>
                          {' '}
                          System access {isBeforeNextDate
                            ? 'will be'
                            : 'was'}{' '}
                          revoked on: {nextDateDisplay}
                        </>
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            </>
          )}

          {!payment && !schedule && (
            <Typography>
              Please add a card and enable your subscription below
            </Typography>
          )}
        </CardContent>

        <CardActions>
          <Button
            onClick={() => {
              setEditingCard(true)
            }}
          >
            {payment ? 'Edit' : 'Add a '} Card
          </Button>
          {schedule?.enabled === '1' ? (
            <Button onClick={() => setCanceling(true)}>
              Disable Subscription
            </Button>
          ) : payment ? (
            <Button
              variant='contained'
              onClick={() => {
                setReenabling(true)
              }}
            >
              {schedule ? 'Reactivate' : 'Activate'} Subscription
            </Button>
          ) : null}
        </CardActions>
      </Card>

      <Dialog open={reenabling}>
        <DialogTitle>
          Are you sure you want to enable your subscription?
        </DialogTitle>
        <DialogContent>
          {!schedule && (
            <Typography>
              You will immediately gain access to the system and your account
              will be charged on{' '}
              <b>
                {new Date(
                  new Date().setDate(new Date().getDate() + 31)
                ).toLocaleDateString()}
              </b>
              .
            </Typography>
          )}
          {isBeforeNextDate && schedule && (
            <Typography>
              Your subscription will pick up where it left off and your next
              payment will be on <b>{nextDateDisplay}</b>.
            </Typography>
          )}
          {!isBeforeNextDate && schedule && (
            <Typography>
              You will immediately regain access to the system and your account
              will be charged <b>today</b>.
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setReenabling(false)
            }}
          >
            Never mind
          </Button>
          <Button
            variant='contained'
            onClick={() => {
              enableSubscription()
            }}
          >
            Enable subscription
          </Button>
        </DialogActions>
      </Dialog>

      <EditCard open={editingCard} setOpen={setEditingCard} refetch={refetch} />

      <DeleteConfirmModal
        isOpen={canceling}
        onClose={() => setCanceling(false)}
        onSubmit={() => cancelSubscription()}
        titleOverride={'Are you sure you want to cancel your subscription?'}
        buttonString={'Cancel Subscription'}
        cancelString={'Never mind'}
      >
        You will still have access to the system until {nextDateDisplay}
      </DeleteConfirmModal>
    </Box>
  )
}

const Wrapper = () => {
  const [tab, setTab] = useState(0)

  const { isAdmin } = useIsAdmin()

  const billingQuery = useGenericEndpoint('/api/billing', {
    shouldPreventCall: !isAdmin,
  })

  const [searchParams] = useSearchParams()

  const isUnpaid = searchParams.get('unpaid') === 'true'

  if (!isAdmin && !isUnpaid)
    return (
      <Box>
        <Typography>You do not have permission to view this page</Typography>
      </Box>
    )

  if (billingQuery.isLoading) return <CircularProgress />

  const today = new Date()
  const nextDate = addTimezoneOffsetToTimestamp(
    new Date(billingQuery.data?.schedule?.next_date)
  )

  const nextDateDisplay = nextDate.toLocaleDateString()

  const isBeforeNextDate = today < nextDate

  return (
    <Box>
      <Tabs sx={{ mb: 2 }} onChange={(_, val) => setTab(val)} value={tab}>
        <Tab label={'Payment Info'} />
        <Tab label={'Transaction History'} />
      </Tabs>

      {tab === 0 && (
        <Billing
          billingQuery={billingQuery}
          nextDateDisplay={nextDateDisplay}
          isBeforeNextDate={isBeforeNextDate}
          isUnpaid={isUnpaid}
        />
      )}
      {tab === 1 && (
        <BillingHistory
          nextDateDisplay={nextDateDisplay}
          billingQuery={billingQuery}
        />
      )}

      {isUnpaid && <UnpaidView />}
    </Box>
  )
}

export default Wrapper
